/**
 * Monitoring Dashboard - HYB MASTER SHOP
 * Minimal analytics view backed by live order data
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Package,
  DollarSign,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Users as UsersIcon,
  CreditCard,
  Wallet as WalletIcon,
  PackageCheck,
} from 'lucide-react';
import { fetchMonitoringOverview, MonitoringOverview } from '../api/admin';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { EmptyState } from '../components/ui/EmptyState';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';
import { cn } from '../utils/responsive';
import { ResponsiveStatsGrid } from '../components/ui/ResponsiveGrid';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    maximumFractionDigits: 0,
  }).format(amount);
}

const LOW_STICKER_THRESHOLD = 50;

export function Monitoring() {
  const [timePeriod, setTimePeriod] = useState<'today' | '7days' | '30days' | 'custom'>('7days');
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const isMobileOrTablet = useIsMobileOrTablet();

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<MonitoringOverview>({
    queryKey: ['monitoring-overview', timePeriod, customDateRange],
    queryFn: () => fetchMonitoringOverview(
      timePeriod,
      timePeriod === 'custom' ? customDateRange.start : undefined,
      timePeriod === 'custom' ? customDateRange.end : undefined
    ),
  });

  const summaryCards = [
    {
      label: 'คำสั่งซื้อทั้งหมด',
      value: `${(data?.summary.totalOrders ?? 0).toLocaleString('th-TH')} รายการ`,
      helper: `วันนี้ ${(data?.summary.ordersToday ?? 0).toLocaleString('th-TH')} รายการ`,
      icon: <Package className="h-5 w-5 text-yellow-500" />,
    },
    {
      label: 'ยอดขายวันนี้',
      value: formatCurrency(data?.summary.revenueToday ?? 0),
      helper: `7 วันล่าสุด ${formatCurrency(data?.summary.revenue7Days ?? 0)}`,
      icon: <DollarSign className="h-5 w-5 text-emerald-500" />,
    },
    {
      label: 'ผู้ใช้ Active 24 ชม.',
      value: `${(data?.users.active24h ?? 0).toLocaleString('th-TH')} คน`,
      helper: `${(data?.users.newToday ?? 0).toLocaleString('th-TH')} สมัครใหม่วันนี้`,
      icon: <Activity className="h-5 w-5 text-blue-500" />,
    },
  ];

  const paymentByMethod = data?.payments.byMethod ?? [];
  const paymentStatus = data?.payments.byStatus ?? [];
  const loyaltyDistribution = data?.users.loyaltyDistribution ?? [];
  const stickerCounts = data?.stickers ?? {};
  const availableStickers = stickerCounts['available'] ?? 0;
  const isLowSticker = availableStickers > 0 && availableStickers < LOW_STICKER_THRESHOLD;
  const topProducts = data?.topProducts ?? [];

  const handlePeriodChange = (value: string) => {
    setTimePeriod(value as any);
    if (value === 'custom') {
      // Default to today when switching to custom
      const today = new Date().toISOString().split('T')[0];
      setCustomDateRange({ start: today, end: today });
    }
  };

  const headerActions = (
    <div className={cn(
      "flex flex-wrap gap-2 lg:gap-3",
      isMobileOrTablet ? "flex-col w-full" : "items-center"
    )}>
      {timePeriod === 'custom' && (
        <div className={cn("flex gap-2", isMobileOrTablet ? "w-full" : "")}>
          <input
            type="date"
            value={customDateRange.start}
            onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
            className={cn(
              "rounded-lg border border-stone-200 bg-white px-3 py-2 lg:py-2.5",
              "text-sm font-medium text-stone-700 shadow-sm transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2",
              "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100",
              isMobileOrTablet ? "flex-1" : "w-auto"
            )}
          />
          <span className="self-center text-stone-400">-</span>
          <input
            type="date"
            value={customDateRange.end}
            onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
            className={cn(
              "rounded-lg border border-stone-200 bg-white px-3 py-2 lg:py-2.5",
              "text-sm font-medium text-stone-700 shadow-sm transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2",
              "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100",
              isMobileOrTablet ? "flex-1" : "w-auto"
            )}
          />
        </div>
      )}

      <select
        value={timePeriod}
        onChange={(e) => handlePeriodChange(e.target.value)}
        className={cn(
          "rounded-lg border border-stone-200 bg-white px-3 lg:px-4 py-2 lg:py-2.5",
          "text-sm font-medium text-stone-700 shadow-sm transition-all duration-200",
          "hover:bg-stone-50 hover:border-yellow-400",
          "focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2",
          "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100",
          "dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900",
          "touch-target",
          isMobileOrTablet && "w-full"
        )}
      >
        <option value="today">วันนี้</option>
        <option value="7days">7 วันล่าสุด</option>
        <option value="30days">30 วันล่าสุด</option>
        <option value="custom">กำหนดเอง</option>
      </select>
      <button
        type="button"
        onClick={() => refetch()}
        className={cn(
          "inline-flex items-center justify-center gap-2",
          "rounded-lg border border-stone-200 bg-white px-3 lg:px-4 py-2 lg:py-2.5",
          "text-sm font-medium text-stone-700 shadow-sm transition-all duration-200",
          "hover:bg-stone-50 hover:border-yellow-400 hover:shadow-accent",
          "active:scale-95",
          "focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2",
          "dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100",
          "dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900",
          "touch-target",
          isMobileOrTablet && "w-full"
        )}
      >
        <RefreshCw className={cn(
          "h-4 w-4",
          isFetching && "animate-spin"
        )} />
        <span>รีเฟรช</span>
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="การเฝ้าระวังระบบ"
          description="ภาพรวมคำสั่งซื้อ การชำระเงิน กระเป๋าเงิน และสติกเกอร์ EMS แบบเรียลไทม์"
          leading={<Activity className="h-6 w-6" />}
          actions={headerActions}
        />
        <Surface className="flex items-center justify-center gap-3">
          <RefreshCw className="h-5 w-5 animate-spin text-yellow-500" />
          <span className="text-sm text-gray-600 dark:text-gray-400">กำลังโหลดข้อมูลภาพรวมการเฝ้าระวัง...</span>
        </Surface>
      </div>
    );
  }

  const overviewContent = error ? (
    <EmptyState
      icon={<AlertCircle className="h-5 w-5" />}
      title="ไม่สามารถโหลดข้อมูลภาพรวมได้"
      description="กรุณาลองรีเฟรชอีกครั้ง หรือแจ้งทีมดูแลระบบหากปัญหายังคงอยู่"
      actions={headerActions}
    />
  ) : (
    <>
      <ResponsiveStatsGrid>
        {summaryCards.map(card => (
          <Surface
            key={card.label}
            padding="lg"
            className={cn(
              "flex h-full flex-col justify-between gap-3",
              "border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent",
              "transition-all duration-200"
            )}
          >
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-gradient-to-br from-yellow-50 to-orange-50 p-2.5 lg:p-3
                            text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-200
                            shadow-sm">
                {card.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs lg:text-sm text-stone-600 dark:text-gray-400 truncate">
                  {card.label}
                </p>
                <p className="text-lg lg:text-xl font-mono font-semibold text-stone-900 dark:text-white mt-0.5">
                  {card.value}
                </p>
              </div>
            </div>
            <p className="text-xs text-stone-500 dark:text-gray-400 line-clamp-2">
              {card.helper}
            </p>
          </Surface>
        ))}
      </ResponsiveStatsGrid>

      <div className="grid gap-3 lg:gap-4 lg:grid-cols-2">
        {/* Users Section */}
        <Surface className="border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">ผู้ใช้งาน</p>
              <h2 className="text-lg lg:text-xl font-mono font-semibold text-stone-900 dark:text-white mt-0.5">
                {(data?.users.total ?? 0).toLocaleString('th-TH')} บัญชี
              </h2>
            </div>
            <div className="rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 p-2.5 lg:p-3 shadow-sm">
              <UsersIcon className="h-5 w-5 lg:h-6 lg:w-6 text-blue-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:gap-4 text-sm text-stone-600 dark:text-gray-300">
            <div className="rounded-lg bg-stone-50 p-3 dark:bg-gray-900/40 border border-stone-100 dark:border-gray-800">
              <p className="text-xs text-stone-500 dark:text-gray-400">สมัครใหม่วันนี้</p>
              <p className="text-base lg:text-lg font-mono font-semibold text-stone-900 dark:text-white mt-1">
                {data?.users.newToday ?? 0}
              </p>
            </div>
            <div className="rounded-lg bg-stone-50 p-3 dark:bg-gray-900/40 border border-stone-100 dark:border-gray-800">
              <p className="text-xs text-stone-500 dark:text-gray-400">Active 24 ชั่วโมง</p>
              <p className="text-base lg:text-lg font-mono font-semibold text-stone-900 dark:text-white mt-1">
                {data?.users.active24h ?? 0}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase text-stone-500 dark:text-gray-400 mb-2">
              Loyalty Level
            </p>
            <div className="space-y-2">
              {loyaltyDistribution.length === 0 && (
                <p className="text-xs text-stone-500 dark:text-gray-400">ยังไม่มีข้อมูลการสะสมแต้ม</p>
              )}
              {loyaltyDistribution.map(item => (
                <div key={item.level} className="flex items-center justify-between text-sm py-1.5">
                  <span className="text-stone-600 dark:text-gray-300">Level {item.level}</span>
                  <span className="font-mono font-medium text-stone-900 dark:text-white">
                    {item.users.toLocaleString('th-TH')} คน
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Surface>

        {/* Wallet Section */}
        <Surface className="border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">กระเป๋าเงิน HYB Wallet</p>
              <h2 className="text-base lg:text-lg font-semibold text-stone-900 dark:text-white mt-0.5">
                ภาพรวมการเติมและใช้จ่าย
              </h2>
            </div>
            <div className="rounded-full bg-gradient-to-br from-amber-50 to-orange-50 p-2.5 lg:p-3 shadow-sm">
              <WalletIcon className="h-5 w-5 lg:h-6 lg:w-6 text-amber-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:gap-4 text-sm text-stone-600 dark:text-gray-300">
            <div className="rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 p-3 border border-amber-100 dark:bg-amber-900/30 dark:border-amber-800/50">
              <p className="text-xs text-amber-700 dark:text-amber-200">ยอดเติมเงิน</p>
              <p className="text-sm lg:text-base font-mono font-semibold text-amber-900 dark:text-amber-100 mt-1">
                {formatCurrency(data?.wallet.topupAmount ?? 0)}
              </p>
              <span className="text-xs text-amber-700 dark:text-amber-200">
                {data?.wallet.topupCount ?? 0} ครั้ง
              </span>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 p-3 border border-emerald-100 dark:bg-emerald-900/30 dark:border-emerald-800/50">
              <p className="text-xs text-emerald-700 dark:text-emerald-200">ค่าใช้จ่ายจาก Wallet</p>
              <p className="text-sm lg:text-base font-mono font-semibold text-emerald-900 dark:text-emerald-100 mt-1">
                {formatCurrency(data?.wallet.spendAmount ?? 0)}
              </p>
            </div>
          </div>
          <p className="mt-3 text-xs text-stone-500 dark:text-gray-400">
            ค่าเฉลี่ยการเติมต่อครั้ง <span className="font-mono font-medium text-stone-900 dark:text-white">
              {formatCurrency(data?.wallet.averageTopup ?? 0)}
            </span>
          </p>
        </Surface>

        {/* Customer Behavior Section */}
        <Surface className="border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">พฤติกรรมลูกค้า</p>
              <h2 className="text-base lg:text-lg font-semibold text-stone-900 dark:text-white mt-0.5">
                Customer Insights
              </h2>
            </div>
            <div className="rounded-full bg-gradient-to-br from-purple-50 to-pink-50 p-2.5 lg:p-3 shadow-sm">
              <UsersIcon className="h-5 w-5 lg:h-6 lg:w-6 text-purple-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:gap-4 text-sm text-stone-600 dark:text-gray-300">
            <div className="rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 p-3 border border-purple-100 dark:bg-purple-900/30 dark:border-purple-800/50">
              <p className="text-xs text-purple-700 dark:text-purple-200">มูลค่าเฉลี่ย/ออเดอร์</p>
              <p className="text-sm lg:text-base font-mono font-semibold text-purple-900 dark:text-purple-100 mt-1">
                {formatCurrency(data?.customerBehavior?.averageOrderValue ?? 0)}
              </p>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 p-3 border border-emerald-100 dark:bg-emerald-900/30 dark:border-emerald-800/50">
              <p className="text-xs text-emerald-700 dark:text-emerald-200">Repeat Purchase</p>
              <p className="text-sm lg:text-base font-mono font-semibold text-emerald-900 dark:text-emerald-100 mt-1">
                {(data?.customerBehavior?.repeatPurchaseRate ?? 0).toFixed(1)}%
              </p>
            </div>
            <div className="col-span-2 rounded-lg bg-stone-50 p-3 border border-stone-100 dark:bg-gray-900/40 dark:border-gray-800">
              <p className="text-xs text-stone-500 dark:text-gray-400 mb-1.5">First Time vs Returning</p>
              <div className="flex justify-between text-xs lg:text-sm">
                <span className="font-mono">
                  First: <span className="font-semibold text-stone-900 dark:text-white">
                    {data?.customerBehavior?.firstTimeVsReturning.firstTime ?? 0}
                  </span>
                </span>
                <span className="font-mono">
                  Return: <span className="font-semibold text-stone-900 dark:text-white">
                    {data?.customerBehavior?.firstTimeVsReturning.returning ?? 0}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-stone-500 dark:text-gray-400">
            CLV: <span className="font-mono font-medium text-stone-900 dark:text-white">
              {formatCurrency(data?.customerBehavior?.customerLifetimeValue ?? 0)}
            </span>
          </p>
        </Surface>

        {/* Payment Methods Section */}
        <Surface className="border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">ช่องทางการชำระเงิน</p>
              <h2 className="text-base lg:text-lg font-semibold text-stone-900 dark:text-white mt-0.5">
                สัดส่วนคำสั่งซื้อที่ชำระแล้ว
              </h2>
            </div>
            <div className="rounded-full bg-gradient-to-br from-purple-50 to-indigo-50 p-2.5 lg:p-3 shadow-sm">
              <CreditCard className="h-5 w-5 lg:h-6 lg:w-6 text-purple-500" />
            </div>
          </div>
          <div className="space-y-2.5 lg:space-y-3">
            {paymentByMethod.length === 0 && (
              <p className="text-sm text-stone-500 dark:text-gray-400">ยังไม่มีข้อมูลการชำระเงิน</p>
            )}
            {paymentByMethod.map(method => (
              <div key={method.method}
                className="flex items-center justify-between text-sm text-stone-600 dark:text-gray-300 py-1.5">
                <span className="capitalize font-medium">{method.method}</span>
                <span className="font-mono text-xs lg:text-sm text-stone-900 dark:text-white">
                  {method.count.toLocaleString('th-TH')} รายการ • {formatCurrency(method.total)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 lg:gap-3 text-xs text-stone-500 dark:text-gray-400">
            {paymentStatus.length === 0 && (
              <div className="col-span-2 rounded-lg bg-stone-50 p-3 text-center text-stone-500 dark:bg-gray-900/40 dark:text-gray-400 border border-stone-100 dark:border-gray-800">
                ยังไม่มีการชำระเงินในระบบ
              </div>
            )}
            {paymentStatus.map(status => (
              <div key={status.status}
                className="rounded-lg bg-stone-50 p-2.5 lg:p-3 dark:bg-gray-900/40 border border-stone-100 dark:border-gray-800">
                <span className="font-semibold text-stone-700 dark:text-gray-100 block mb-1">
                  {status.status}
                </span>
                <div className="font-mono text-stone-900 dark:text-white">
                  {status.count.toLocaleString('th-TH')} รายการ
                </div>
              </div>
            ))}
          </div>
        </Surface>

        {/* EMS Sticker Section */}
        <Surface className="border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">สถานะสติกเกอร์ EMS</p>
              <h2 className="text-base lg:text-lg font-mono font-semibold text-stone-900 dark:text-white mt-0.5">
                คงเหลือ {availableStickers.toLocaleString('th-TH')} ใบ
              </h2>
            </div>
            <div className="rounded-full bg-gradient-to-br from-indigo-50 to-purple-50 p-2.5 lg:p-3 shadow-sm">
              <PackageCheck className="h-5 w-5 lg:h-6 lg:w-6 text-indigo-500" />
            </div>
          </div>
          {isLowSticker && (
            <div className="mb-3 rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2.5 text-xs lg:text-sm text-amber-800 dark:border-amber-500/50 dark:bg-amber-900/30 dark:text-amber-200 shadow-sm">
              <span className="font-semibold flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                ใกล้หมด!
              </span> กรุณาเติมสติกเกอร์ EMS ให้เพียงพอก่อนปริ้นใบปะหน้า
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 lg:gap-4 text-sm text-stone-600 dark:text-gray-300">
            {Object.entries(stickerCounts).map(([status, count]) => (
              <div key={status}
                className="rounded-lg bg-stone-50 p-3 dark:bg-gray-900/40 border border-stone-100 dark:border-gray-800">
                <p className="text-xs text-stone-500 dark:text-gray-400 capitalize mb-1">{status}</p>
                <p className="text-base lg:text-lg font-mono font-semibold text-stone-900 dark:text-white">
                  {count.toLocaleString('th-TH')}
                </p>
              </div>
            ))}
          </div>
        </Surface>
      </div>

      {/* Top Products Section */}
      <Surface className="border-stone-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-500 hover:shadow-accent transition-all duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs lg:text-sm font-medium text-stone-600 dark:text-gray-400">สินค้ายอดนิยม</p>
            <h2 className="text-base lg:text-lg font-semibold text-stone-900 dark:text-white mt-0.5">
              Top 5 จากจำนวนการสั่งซื้อ
            </h2>
          </div>
          <div className="rounded-full bg-gradient-to-br from-yellow-50 to-orange-50 p-2.5 lg:p-3 shadow-sm">
            <Package className="h-5 w-5 lg:h-6 lg:w-6 text-yellow-500" />
          </div>
        </div>

        {/* Mobile: Card View */}
        {isMobileOrTablet ? (
          <div className="space-y-3">
            {topProducts.length === 0 ? (
              <div className="rounded-lg bg-stone-50 p-6 text-center text-stone-500 dark:bg-gray-900/40 dark:text-gray-400 border border-stone-100 dark:border-gray-800">
                ยังไม่มีคำสั่งซื้อที่เพียงพอสำหรับการจัดอันดับ
              </div>
            ) : (
              topProducts.map((product, index) => (
                <div key={`mobile-product-${index}-${product.product}`}
                  className="rounded-lg bg-stone-50 p-3 border border-stone-100 dark:bg-gray-900/40 dark:border-gray-800
                              hover:border-yellow-400 hover:shadow-sm transition-all duration-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold">
                        {index + 1}
                      </div>
                      <p className="font-medium text-stone-900 dark:text-white text-sm line-clamp-2">
                        {product.product}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-stone-200 dark:border-gray-700">
                    <div className="text-xs text-stone-600 dark:text-gray-300">
                      จำนวน: <span className="font-mono font-semibold text-stone-900 dark:text-white">
                        {product.quantity.toLocaleString('th-TH')}
                      </span>
                    </div>
                    <div className="text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(product.revenue)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Desktop: Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-stone-200 text-sm dark:divide-gray-700">
              <thead className="bg-stone-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-stone-600 dark:text-gray-400">
                    สินค้า
                  </th>
                  <th className="px-4 py-2.5 text-right font-semibold text-stone-600 dark:text-gray-400">
                    จำนวน
                  </th>
                  <th className="px-4 py-2.5 text-right font-semibold text-stone-600 dark:text-gray-400">
                    ยอดขาย
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200 dark:divide-gray-700">
                {topProducts.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-stone-500 dark:text-gray-400">
                      ยังไม่มีคำสั่งซื้อที่เพียงพอสำหรับการจัดอันดับ
                    </td>
                  </tr>
                )}
                {topProducts.map((product, index) => (
                  <tr key={`desktop-product-${index}-${product.product}`}
                    className="hover:bg-stone-50 dark:hover:bg-gray-900/20 transition-colors">
                    <td className="px-4 py-3 text-stone-700 dark:text-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold">
                          {index + 1}
                        </div>
                        {product.product}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-stone-700 dark:text-gray-200">
                      {product.quantity.toLocaleString('th-TH')}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(product.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Surface>
    </>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="การเฝ้าระวังระบบ"
        description="ภาพรวมคำสั่งซื้อ การชำระเงิน กระเป๋าเงิน และสติกเกอร์ EMS แบบเรียลไทม์"
        leading={<Activity className="h-6 w-6" />}
        actions={headerActions}
      />
      {overviewContent}
    </div>
  );
}
